import { Checkbox, FormControlLabel, Grid } from "@material-ui/core";
import { LcmModel } from "../../domain/LcmModel";
import { SectionProps } from "./SectionProps";
import { Controller } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { nameof } from "ts-simple-nameof";
import { fieldLabels } from "./fieldlabels";
import { Error } from "../common/Error";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { Select } from "@ofqual/portal-components/components/Select";
import {
  directorateLookupService,
  matterTypeHierarchyLookupService,
  usersService,
} from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { UserRole, UserType } from "../../redux/types";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { useEffect, useState} from "react";

export const MainDetails: React.FC<{
  sectionProps: SectionProps<LcmModel>;
}> = ({ sectionProps }) => {
  const { control, errors, disabled, watch, setValue, getValues, item, lookupTracker, formState } =
    sectionProps;
  const { roles, upn } = useSelector(authSelector);

  const disableAssign =
    disabled ||
    !(
      roles.includes(UserRole.Director) ||
      roles.includes(UserRole.LPM) ||
      roles.includes(UserRole.CaseManager) ||
      roles.includes(UserRole.GC)
    );
  
  const [matterChanged, setMatterChanged] = useState<boolean>(false); 
  const [categoryChanged, setCategoryChanged] = useState<boolean>(false); 

  const { dirtyFields } = formState;
  
  useEffect(() => {
    null
   }, [matterChanged]);

  useEffect(() => {
    null
  }, [categoryChanged]);

  

  const thisItem = typeof item === "undefined" ? null : item;

  const userType = UserType.Ofqual;
  const [
    matterTypeLookups,
    legalLookups,
    userLookups,
    directorateLookups,
  ] = useLookup(
    matterTypeHierarchyLookupService,
    {
      service: usersService,
      id: "upn",
      label: (item) => (item.fullName ? item.fullName : item.upn),
      params: {
        userType,
        role: [
          UserRole.Director,
          UserRole.CaseManager,
          UserRole.GC,
          UserRole.LPM,
        ],
        active: true,
      },
    },
    {
      service: usersService,
      id: "upn",
      label: (item) => (item.fullName ? item.fullName : item.upn),
      params: {
        userType,
        active: true,
      },
    },
    directorateLookupService
  );

  const watchDateAssigned = watch(
    nameof<LcmModel>((x) => x.assignedCreatedDate)
  );

  const distinctMatterTypes: any[] = matterTypeLookups.filter((value, index, array) => {
    const foundItem = array.find(t => t.matterTypeID === value.matterTypeID);
    if (foundItem) {
      return array.indexOf(foundItem) === index;
    }
    return;
  }).map(x => {
    if (x) {
      return { id: x.matterTypeID, label: x.matterTypeLookupValue };
    }
    return;
  });

  const CatagoryTypes: any[] =
    matterTypeLookups
      .filter((matterType) => getValues("matterType") === matterType.matterTypeID)
      .filter((value, index, array) => {
        const foundItem = array.find(t => t.categoryID === value.categoryID);
        if (foundItem) {
          return array.indexOf(foundItem) === index;
        }
        return;
      })
      .map((x) => {
        if (x) {
          return { id: x.categoryID, label: x.categoryLookupValue };
        }
        return;
      })
  
  const SubCatagoryTypes: any[] = matterTypeLookups
    .filter((matterType) => getValues("category") === matterType.categoryID)
    .filter((value, index, array) => {
      const foundItem = array.find(
        (t) => t.subCategoryID === value.subCategoryID
      );
      if (foundItem && foundItem.subCategoryID !== null) {
        return array.indexOf(foundItem) === index;
      }
      return;
    })
    .map((x) => {
      if (x) {
        return { id: x.subCategoryID, label: x.subCategoryLookupValue };
      }
      return;
    })

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12} xl={8}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.caseTitle)}
            rules={{ required: true }}
            as={
              <TextField
                variant="outlined"
                label={fieldLabels.caseTitle}
                data-test="case-title-field"
                fullWidth
                readOnly={disabled}
                required
              />
            }
          />
          {errors.caseTitle?.type === "required" && (
            <Error data-test="case-title-validation">
              The case title field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.assignedCreatedDate)}
            as={
              <DatePicker
                label={fieldLabels.dateAssigned}
                dataTest="date-assigned-field"
                readOnly={disabled}
                clearable
                returnDayBoundary="endOfDay"
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.summary)}
            render={({ onChange, value }) => (
              <>
                <label>{fieldLabels.summary}</label>
                <MarkdownEditor
                  onChange={onChange}
                  value={value}
                  readOnly={disabled}
                  dataTest="summary-field"
                />
              </>
            )}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.directorate)}
            rules={{ validate: (value) => !!value.length }}
            disabled={disabled}
            as={
              <Select
                lookupTracker={lookupTracker}
                required
                label={fieldLabels.directorate}
                datatest="directorate-field"
                selectVariant={"multiple"}
              >
                {directorateLookups}
              </Select>
            }
          />
          {errors.directorate && (
            <Error data-test="directorate-validation">
              This field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.team)}
            rules={{ required: true }}
            as={
              <TextField
                variant="outlined"
                label={fieldLabels.team}
                data-test="team-field"
                fullWidth
                readOnly={disabled}
                required
              />
            }
          />
          {errors.team?.type === "required" && (
            <Error data-test="team-validation">
              The team field is required
            </Error>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.requestedBy)}
            as={
              <Select
                lookupTracker={lookupTracker}
                label={fieldLabels.requested}
                disabled={disabled}
                datatest="requested-field"
              >
                {userLookups}
              </Select>
            }
          />
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.matterType)}
            defaultValue={null}
            render={(props) => {
              const _onChange = (event: any) => {
                // reset other value
                setMatterChanged(!matterChanged);
                setValue("category", null);
                setValue("subCategory", null);
                dirtyFields.category = true;
                dirtyFields.subCategory = true;
                props.onChange(event);
              };
              return (
                <Select
                  {...props}
                  label={fieldLabels.matterType}
                  datatest="matter-type-field"
                  onChange={_onChange}
                  lookupTracker={lookupTracker}
                >
                  {distinctMatterTypes}
                </Select>
              );
            }}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.category)}
            defaultValue={null}
            render={(props) => {
              const _onChange = (event: any) => {
                // reset other value
                setCategoryChanged(!categoryChanged);
                setValue("subCategory", null);
                dirtyFields.subCategory = true;
                props.onChange(event);
              };
              return (
                <Select
                  {...props}
                  label={fieldLabels.category}
                  datatest="category-field"
                  onChange={_onChange}
                  lookupTracker={lookupTracker}
                  disabled={CatagoryTypes.length > 0 ? false : true}
                >
                  {CatagoryTypes ?? { id: -1, label: "n/a" }}
                </Select>
              );
            }}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={4}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.subCategory)}
            as={
              <Select
                lookupTracker={lookupTracker}
                label={fieldLabels.subCategory}
                disabled={SubCatagoryTypes.length > 0 ? false : true}
                datatest="sub-category-field"
              >
                {SubCatagoryTypes}
              </Select>
            }
          />
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.dueDate)}
            as={
              <DatePicker
                label={fieldLabels.dueDate}
                dataTest="due-date-field"
                readOnly={disabled}
                clearable
                returnDayBoundary="endOfDay"
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.assignedTo)}
            render={({ onChange, value }) => (
              <Select
                lookupTracker={lookupTracker}
                label={fieldLabels.assingedTo}
                disabled={disableAssign}
                datatest="assigned-users-field"
                name={nameof<LcmModel>((x) => x.assignedTo)}
                selectVariant={"multiple"}
                onChange={(e) => {
                  const x: any = e.target;
                  const val: string[] = x.value;

                  const removed = value.filter((x: string) => !val.includes(x));
                  const added = val.filter((x: string) => !value.includes(x));

                  if (watchDateAssigned === null && val.length !== 0) {
                    setValue("assignedCreatedDate", new Date().toString());
                  }

                  if (
                    roles.includes(UserRole.LPM) ||
                    roles.includes(UserRole.GC) ||
                    roles.includes(UserRole.Director)
                  ) {
                    onChange(e.target.value);
                  } else {
                    if (
                      removed.findIndex(
                        (m: string) => m.toLowerCase() === upn.toLowerCase()
                      ) > -1 ||
                      added.findIndex(
                        (m: string) => m.toLowerCase() === upn.toLowerCase()
                      ) > -1
                    ) {
                      onChange(e.target.value);
                    } else if (roles.includes(UserRole.CaseManager)) {
                      alert(
                        "You only have authority to add or remove yourself from the list of assigned users"
                      );
                    }
                  }
                }}
                value={value}
              >
                {roles.includes(UserRole.CaseManager) &&
                (thisItem === null || thisItem?.assignedTo.length === 0)
                  ? legalLookups.filter(
                      (x) => x.id.toString().toLowerCase() === upn.toLowerCase()
                    )
                  : legalLookups}
              </Select>
            )}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name={nameof<LcmModel>((x) => x.lineManagerUpn)}
            rules={{ required: true }}
            as={
              <Select
                lookupTracker={lookupTracker}
                label={fieldLabels.lineManager}
                disabled={disabled}
                datatest="line-manager-field"
                required
              >
                {legalLookups}
              </Select>
            }
          />
          {errors.lineManagerUpn?.type === "required" && (
            <Error data-test="line-manager-validation">
              The line manager field is required
            </Error>
          )}
        </Grid>
        {roles.includes(UserRole.GC) ? (
          <Grid item xs={12}>
            <Controller
              control={control}
              name="sensitive"
              render={(props) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e: any) => {
                        props.onChange(e.target.checked);
                      }}
                      checked={props.value}
                      disabled={disabled}
                      data-test="legal-gc-only-sensitive-checkbox"
                    />
                  }
                  label={fieldLabels.sensitive}
                />
              )}
            />
          </Grid>
        ) : null}
      </Grid>
    );
};
